/**
 * PMWorkOrderReadback - Tiered Validation Display
 *
 * Shows parsed Excel data with three-tier validation:
 * 1. Critical Blockers (red) - Must fix to publish
 * 2. Warnings (yellow) - Can publish, but highlighted
 * 3. Reference (collapsible) - Full parsed data
 */

import { useState, useEffect, useCallback } from "react";
import PMPublishStateIndicator from "./PMPublishStateIndicator";

export default function PMWorkOrderReadback({
  acquireAccessToken,
  workOrder,
  onClose,
  onPublish,
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [readbackData, setReadbackData] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    blockers: true,
    warnings: true,
    skus: false,
    equipment: false,
    proofing: false,
  });
  const [syncing, setSyncing] = useState(false);
  const [publishing, setPublishing] = useState(false);

  // Load readback data
  const loadReadback = useCallback(async () => {
    if (!workOrder?.work_order_id && !workOrder?.id) {
      setError("No work order ID provided");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const accessToken = await acquireAccessToken();
      if (!accessToken) {
        setError("Authentication required");
        return;
      }

      const woId = workOrder.work_order_id || workOrder.id;
      const res = await fetch(`/api/pm-center/work-orders/${woId}/readback`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || "Failed to load readback");
      }

      const data = await res.json();
      setReadbackData(data);
      setError(null);
    } catch (err) {
      console.error("Error loading readback:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [workOrder, acquireAccessToken]);

  // Initial load
  useEffect(() => {
    loadReadback();
  }, [loadReadback]);

  // Handle sync
  const handleSync = async () => {
    try {
      setSyncing(true);
      const accessToken = await acquireAccessToken();
      if (!accessToken) return;

      const woId = workOrder.work_order_id || workOrder.id;
      const res = await fetch(`/api/pm-center/work-orders/${woId}/sync`, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || "Sync failed");
      }

      const result = await res.json();
      alert(`Sync complete!\n\nBlockers: ${result.validation.blockerCount}\nWarnings: ${result.validation.warningCount}`);

      // Reload readback data
      await loadReadback();
    } catch (err) {
      console.error("Error syncing:", err);
      alert(`Sync error: ${err.message}`);
    } finally {
      setSyncing(false);
    }
  };

  // Handle publish
  const handlePublish = async () => {
    if (!readbackData?.validation?.canPublish) {
      alert("Cannot publish: work order must be in VALIDATED state with no blockers.");
      return;
    }

    if (!confirm("Publish this work order to production?")) {
      return;
    }

    try {
      setPublishing(true);
      const accessToken = await acquireAccessToken();
      if (!accessToken) return;

      const woId = workOrder.work_order_id || workOrder.id;
      const res = await fetch(`/api/pm-center/work-orders/${woId}/publish`, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || "Publish failed");
      }

      alert("Work order published to production!");
      onPublish?.();
    } catch (err) {
      console.error("Error publishing:", err);
      alert(`Publish error: ${err.message}`);
    } finally {
      setPublishing(false);
    }
  };

  // Toggle section
  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading work order data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="text-center">
          <div className="text-red-500 text-4xl mb-4">!</div>
          <p className="text-red-600 font-medium mb-2">Failed to load work order</p>
          <p className="text-gray-500 text-sm mb-4">{error}</p>
          <div className="flex gap-2 justify-center">
            <button
              onClick={loadReadback}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Try Again
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { workOrder: wo, parsedData, validation } = readbackData || {};
  const blockers = validation?.blockers || [];
  const warnings = validation?.warnings || [];

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-white border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h2 className="font-semibold text-gray-900">
                {wo?.wo_number || workOrder?.wo_number || "Work Order"}
              </h2>
              <p className="text-sm text-gray-500">
                {workOrder?.project_name || workOrder?.description}
              </p>
            </div>
          </div>

          {/* State Indicator + Actions */}
          <div className="flex items-center gap-3">
            <PMPublishStateIndicator workOrder={wo || workOrder} />

            <button
              onClick={handleSync}
              disabled={syncing || wo?.publish_state === "PUBLISHED"}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {syncing ? "Syncing..." : "Sync from SharePoint"}
            </button>

            <button
              onClick={handlePublish}
              disabled={publishing || !validation?.canPublish}
              className={`px-4 py-2 rounded-lg font-medium ${
                validation?.canPublish
                  ? "bg-green-600 text-white hover:bg-green-700"
                  : "bg-gray-200 text-gray-400 cursor-not-allowed"
              }`}
            >
              {publishing ? "Publishing..." : "Publish to Production"}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* TIER 1: Critical Blockers */}
        {blockers.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg overflow-hidden">
            <button
              onClick={() => toggleSection("blockers")}
              className="w-full px-4 py-3 flex items-center justify-between bg-red-100 hover:bg-red-150"
            >
              <div className="flex items-center gap-2">
                <span className="text-red-600 text-xl">🚫</span>
                <span className="font-semibold text-red-800">
                  Critical Blockers ({blockers.length})
                </span>
              </div>
              <svg
                className={`w-5 h-5 text-red-600 transition-transform ${expandedSections.blockers ? "rotate-180" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {expandedSections.blockers && (
              <div className="px-4 py-3 space-y-2">
                <p className="text-xs text-red-600 italic mb-2">
                  These must be resolved before publishing
                </p>
                {blockers.map((blocker, idx) => (
                  <div key={idx} className="flex items-start gap-2 p-2 bg-white rounded border border-red-200">
                    <span className="text-red-500 mt-0.5">•</span>
                    <div>
                      <p className="text-sm text-gray-900">{blocker.message}</p>
                      {blocker.field_name && (
                        <p className="text-xs text-gray-500">
                          Field: {blocker.field_name}
                          {blocker.sku_number && ` (SKU #${blocker.sku_number})`}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* TIER 2: Warnings */}
        {warnings.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg overflow-hidden">
            <button
              onClick={() => toggleSection("warnings")}
              className="w-full px-4 py-3 flex items-center justify-between bg-yellow-100 hover:bg-yellow-150"
            >
              <div className="flex items-center gap-2">
                <span className="text-yellow-600 text-xl">⚠️</span>
                <span className="font-semibold text-yellow-800">
                  Warnings ({warnings.length})
                </span>
              </div>
              <svg
                className={`w-5 h-5 text-yellow-600 transition-transform ${expandedSections.warnings ? "rotate-180" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {expandedSections.warnings && (
              <div className="px-4 py-3 space-y-2">
                <p className="text-xs text-yellow-600 italic mb-2">
                  Publishing allowed, but review these items
                </p>
                {warnings.map((warning, idx) => (
                  <div key={idx} className="flex items-start gap-2 p-2 bg-white rounded border border-yellow-200">
                    <span className="text-yellow-500 mt-0.5">•</span>
                    <div>
                      <p className="text-sm text-gray-900">{warning.message}</p>
                      {warning.field_name && (
                        <p className="text-xs text-gray-500">
                          Field: {warning.field_name}
                          {warning.sku_number && ` (SKU #${warning.sku_number})`}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Valid indicator when no blockers */}
        {blockers.length === 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3">
            <div className="flex items-center gap-2">
              <span className="text-green-600 text-xl">✓</span>
              <span className="font-medium text-green-800">
                Ready to Publish
              </span>
              <span className="text-sm text-green-600">
                {warnings.length > 0 ? `(${warnings.length} warnings)` : "No issues found"}
              </span>
            </div>
          </div>
        )}

        {/* TIER 3: Reference Data (collapsed by default) */}
        {parsedData && (
          <>
            {/* SKUs */}
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection("skus")}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50"
              >
                <div className="flex items-center gap-2">
                  <span className="text-gray-600">📦</span>
                  <span className="font-medium text-gray-700">
                    SKUs & POAs ({parsedData.poas?.length || 0})
                  </span>
                </div>
                <svg
                  className={`w-5 h-5 text-gray-400 transition-transform ${expandedSections.skus ? "rotate-180" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {expandedSections.skus && parsedData.poas && (
                <div className="border-t border-gray-100">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">SKU #</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">Description</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">Qty</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">POA Type</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">Substrate</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {parsedData.poas.map((poa, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-4 py-2 font-mono">{poa.sku_number}</td>
                          <td className="px-4 py-2 truncate max-w-[200px]">{poa.sku_description}</td>
                          <td className="px-4 py-2">{poa.sku_quantity}</td>
                          <td className="px-4 py-2">{poa.poa_type || "—"}</td>
                          <td className="px-4 py-2 truncate max-w-[150px]">{poa.substrate || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Equipment */}
            {parsedData.equipment?.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleSection("equipment")}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600">🖨️</span>
                    <span className="font-medium text-gray-700">
                      Equipment ({parsedData.equipment.length})
                    </span>
                  </div>
                  <svg
                    className={`w-5 h-5 text-gray-400 transition-transform ${expandedSections.equipment ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {expandedSections.equipment && (
                  <div className="border-t border-gray-100 p-4">
                    <div className="grid gap-2">
                      {parsedData.equipment.map((eq, idx) => (
                        <div key={idx} className="flex items-center gap-4 text-sm">
                          <span className="font-mono text-gray-600">SKU #{eq.sku_number}</span>
                          <div className="flex gap-2">
                            {eq.production_art_required && (
                              <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">PA</span>
                            )}
                            {eq.epson && (
                              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">Epson</span>
                            )}
                            {eq.silver && (
                              <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">Silver</span>
                            )}
                            {eq.uv && (
                              <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs">UV</span>
                            )}
                            {eq.opti_required && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">Opti</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Proofing */}
            {parsedData.proofing?.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleSection("proofing")}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600">📝</span>
                    <span className="font-medium text-gray-700">
                      Proofing ({parsedData.proofing.length})
                    </span>
                  </div>
                  <svg
                    className={`w-5 h-5 text-gray-400 transition-transform ${expandedSections.proofing ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {expandedSections.proofing && (
                  <div className="border-t border-gray-100 p-4">
                    <div className="grid gap-2">
                      {parsedData.proofing.map((p, idx) => (
                        <div key={idx} className="text-sm">
                          <span className="font-mono text-gray-600">SKU #{p.sku_number}:</span>
                          <span className="ml-2">
                            {p.client_approval_required ? "Client Approval" : "Internal Review"}
                            {p.proofing_notes && ` - ${p.proofing_notes}`}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* No parsed data yet */}
        {!parsedData && !loading && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-8 text-center">
            <p className="text-gray-500 mb-4">
              No Excel data has been synced yet.
            </p>
            <button
              onClick={handleSync}
              disabled={syncing}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              {syncing ? "Syncing..." : "Sync from SharePoint"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
